import type { ColumnConfig } from '@/components/table/Table';
import { IconButton, Tooltip } from '@mui/material';
import { Table } from '@/components';
import {
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
  WEBHOOK_DELIVERY_STATUS_LABELS,
  webhookStatusLabel,
  type IntegrationWebhook,
  type IntegrationWebhookLastDelivery,
} from '../../../models/IntegrationWebhook';
import { maskedWebhookHeaders } from '../../../models/WebhookHeaders';

const formatDateTime = (value: string) => new Date(value).toLocaleString('es-UY');

const LastDelivery = ({ delivery }: { delivery: IntegrationWebhookLastDelivery | null }) => {
  if (!delivery) return <span className='text-gray-500'>Sin envíos</span>;
  const failed = delivery.status !== 'SENT' && !!delivery.errorMessage;
  return (
    <span className='flex flex-col'>
      <span className={failed ? 'text-red-700' : 'text-gray-800'}>
        {WEBHOOK_DELIVERY_STATUS_LABELS[delivery.status] ?? delivery.status}
        {delivery.httpStatus != null && ` · HTTP ${delivery.httpStatus}`}
      </span>
      <span className='text-xs text-gray-500'>
        Pedido {delivery.shippingNumber} · {formatDateTime(delivery.createdAt)}
      </span>
      {delivery.errorMessage && (
        <span className='text-xs text-red-600 line-clamp-2 break-all' title={delivery.errorMessage}>
          {delivery.errorMessage}
        </span>
      )}
    </span>
  );
};

const columns: ColumnConfig[] = [
  {
    field: 'status',
    label: 'Estado',
    render: (_: unknown, row: IntegrationWebhook) => webhookStatusLabel(row.status),
  },
  {
    field: 'urls',
    label: 'URL',
    render: (urls: string[]) => (
      <span className='flex flex-col break-all'>
        {urls.length ? urls.map((url) => <span key={url}>{url}</span>) : '-'}
      </span>
    ),
  },
  {
    field: 'headerNames',
    label: 'Headers',
    render: (headerNames: string[]) => maskedWebhookHeaders(headerNames),
  },
  {
    field: 'active',
    label: 'Activo',
    render: (active: boolean, row: IntegrationWebhook) => (
      <span className='flex flex-col'>
        <span className={active ? 'text-green-700' : 'text-gray-500'}>{active ? 'Sí' : 'No'}</span>
        {row.hasBodyTemplate && <span className='text-xs text-gray-500'>Plantilla propia</span>}
        {row.requiresTokenRefresh && <span className='text-xs text-gray-500'>Token renovable</span>}
      </span>
    ),
  },
  {
    field: 'lastDelivery',
    label: 'Último envío',
    render: (delivery: IntegrationWebhookLastDelivery | null) => <LastDelivery delivery={delivery} />,
  },
];

interface IntegrationWebhooksTableProps {
  webhooks: IntegrationWebhook[];
  isLoading: boolean;
  disabled: boolean;
  onEdit: (webhook: IntegrationWebhook) => void;
  onToggleActive: (webhook: IntegrationWebhook) => void;
  onRemove: (webhook: IntegrationWebhook) => void;
}

const IntegrationWebhooksTable = ({
  webhooks,
  isLoading,
  disabled,
  onEdit,
  onToggleActive,
  onRemove,
}: IntegrationWebhooksTableProps) => {
  if (!isLoading && webhooks.length === 0) {
    return <div className='text-center text-gray-500 py-8'>El comercio no tiene webhooks</div>;
  }

  return (
    <Table
      data={webhooks}
      columns={columns}
      isLoading={isLoading}
      skeletonRows={3}
      renderActions={(row: IntegrationWebhook) => (
        <div className='flex'>
          <Tooltip title='Editar'>
            <IconButton size='small' onClick={() => onEdit(row)} disabled={disabled}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.active ? 'Desactivar' : 'Activar'}>
            <IconButton size='small' onClick={() => onToggleActive(row)} disabled={disabled}>
              {row.active ? <ToggleOnIcon fontSize='small' /> : <ToggleOffIcon fontSize='small' />}
            </IconButton>
          </Tooltip>
          <Tooltip title='Borrar'>
            <IconButton size='small' onClick={() => onRemove(row)} disabled={disabled}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )}
    />
  );
};

export default IntegrationWebhooksTable;
